import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  type CancellableEventResponse,
  type DashboardEvent,
  type DrillMenuEvent,
  type LookerEmbedDashboard,
  type LookerEmbedFilterParams,
  LookerEmbedSDK,
} from '@looker/embed-sdk';
import { keyBy, mapValues, pickBy } from 'lodash';
import { useBoolean } from 'usehooks-ts';
import { v7 as uuidV7 } from 'uuid';

import { LoadingOverlay } from '@/components/ui/old/LoadingOverlay';
import { appConfig } from '@/config';
import { useLookerDashboardId } from '@/hooks/looker/use-looker-dashboard-id';
import { useAuth } from '@/hooks/use-auth';
import { usePageContext } from '@/providers/page-context';

// Only pass defined filters to Looker; undefined values should be omitted so
// Looker can apply dashboard-level defaults (e.g. "30 day" for timeframes).
function cleanFilters(
  filters?: Record<string, string | undefined>,
): LookerEmbedFilterParams {
  const entries = Object.entries(filters ?? {}).filter(
    ([, value]) => value !== undefined,
  );
  return Object.fromEntries(entries) as LookerEmbedFilterParams;
}

const linksPerDashboardId = keyBy(
  appConfig.linksFromDashboard,
  (config) => config.dashboardId,
);

export type DashboardProps = {
  id: string;
  actions?: ReactNode;
  filters?: Record<string, string | undefined>;
  onFiltersChange?: (filters: Record<string, string | undefined>) => void;
};

export function Dashboard({
  id,
  filters,
  actions,
  onFiltersChange,
}: DashboardProps) {
  const dashboardRef = useRef<LookerEmbedDashboard | null>(null);
  const getDashboardId = useLookerDashboardId();
  const history = useHistory();
  const loading = useBoolean(false);
  const auth = useAuth();
  const { setFilters } = usePageContext();

  const dashboardId = getDashboardId(id);

  // Remove undefined properties (Looker doesnt' like top_id: undefined)
  const cleanedFilters = useMemo(
    () => cleanFilters(filters) as LookerEmbedFilterParams,
    [filters],
  );

  useEffect(
    function reloadFilters() {
      dashboardRef.current?.updateFilters(cleanedFilters);
      dashboardRef.current?.send('dashboard:run');
    },
    [cleanedFilters],
  );

  useEffect(
    function reloadDashboard() {
      dashboardRef.current?.loadDashboard(dashboardId);
    },
    [dashboardId],
  );

  /**
   * This function is called when the user click on a link inside the Looker dashboard
   * We intercept the click and if we find an adequate configuration in `src/config.ts`, we redirect the user to the correct page
   **/
  const handleDrillMenuClick = useCallback(
    function (event: DrillMenuEvent): CancellableEventResponse {
      // 1. Get the dashboardId
      const targetUrl = new URL(
        event.url,
        window.location.origin, // This value is not used, but it's required by the URL constructor
      );

      // pathname looks like: /platform_customer_staging::{dashboardId}
      const currentPath = targetUrl.pathname.match(/::(.*?)$/);

      if (!currentPath) {
        return { cancel: false };
      }

      const [, dashboardId] = currentPath;

      // 2. Get the configuration for this dashboard
      const dashboardConfig = linksPerDashboardId[dashboardId];

      if (!dashboardConfig) {
        console.error(
          `The webapp intercept a drillmenu click from the dashboard "${dashboardId}", but it's not configured in the config.ts`,
        );

        return { cancel: false };
      }

      // 3. Take the selected queryParams to the new URL
      const redirectQueryParams = new URLSearchParams();

      for (const [prop, value] of targetUrl.searchParams.entries()) {
        if (dashboardConfig.queryParams.includes(prop)) {
          redirectQueryParams.set(prop, value);
        }
      }

      // 4. redirect the user
      history.push({
        pathname: dashboardConfig.redirectTo,
        search: redirectQueryParams.toString(),
      });

      return { cancel: true };
    },
    [history],
  );

  const initDashboard = useCallback(
    async function (el: HTMLDivElement | null) {
      if (!el) {
        return;
      }

      el.innerHTML = '';

      loading.setTrue();

      const sessionId = uuidV7();

      LookerEmbedSDK.initCookieless(
        'accure.cloud.looker.com',
        async function acquireEmbedSession() {
          const accessToken = await auth.getAccessToken();
          const result = await fetch(
            `${appConfig.bffUrl}/looker/acquire-embed-session`,
            {
              method: 'GET',
              headers: {
                'x-looker-embed-session-id': sessionId,
                authorization: `Bearer ${accessToken}`,
              },
            },
          );
          return result.json();
        },
        async function generateEmbedToken() {
          const accessToken = await auth.getAccessToken();
          const result = await fetch(
            `${appConfig.bffUrl}/looker/generate-embed-tokens`,
            {
              method: 'GET',
              headers: {
                'x-looker-embed-session-id': sessionId,
                authorization: `Bearer ${accessToken}`,
              },
            },
          );
          return result.json();
        },
      );

      LookerEmbedSDK.createDashboardWithId(dashboardId)
        .withNext()
        .appendTo(el)
        .withFilters(cleanedFilters)
        .on('dashboard:loaded', function (event: DashboardEvent) {
          loading.setFalse();
          setFilters(
            pickBy(event.dashboard.dashboard_filters, (value) =>
              Boolean(value),
            ),
          );
        })
        .on('drillmenu:click', handleDrillMenuClick)
        .on('dashboard:filters:changed', function (event: DashboardEvent) {
          onFiltersChange?.(
            mapValues(
              event.dashboard.dashboard_filters,
              (value) => value || undefined,
            ),
          );
        })
        .on('session:status', function ({ expired }) {
          if (expired) {
            window.location.reload();
          }
        })
        .build()
        .connect()
        .then(function (dashboard) {
          dashboardRef.current = dashboard;
        })
        .catch(function (error) {
          console.error('Looker dashboard init', error);
        });
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <LoadingOverlay loading={loading.value}>
      <div className="grid size-full grid-rows-[1fr_min-content]">
        <div className="looker-dashboard size-full" ref={initDashboard} />
        {actions ? (
          <div className="flex w-full flex-col items-center gap-4 border-t border-gray-100 p-4">
            {actions}
          </div>
        ) : null}
      </div>
    </LoadingOverlay>
  );
}
